// src/Dashboard/Messages.js
import React, { useState, useEffect } from "react";
import api from "./Api.js";
import "./Messages.css";
import {
  FiMail,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiMessageSquare,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("");

  // ✅ Fetch Messages
  const fetchMessages = async (page = 1, search = "", status = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: "10",
        ...(search && { search }),
        ...(status && { status }),
      });

      const res = await api.get(`/api/ContactUs?${params}`);
      if (res.data.success) {
        setMessages(res.data.data.reverse());
        setTotalPages(res.data.pagination.totalPages);
        setTotalCount(res.data.pagination.totalCount);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
      alert("❌ Failed to fetch messages");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch status counts
  const fetchStatusCounts = async () => {
    try {
      const res = await api.get("/api/ContactUs/status-counts");
      if (res.data.success) {
        const counts = {};
        res.data.data.forEach((item) => {
          counts[item.Status] = item.Count;
        });
        setStatusCounts(counts);
      }
    } catch (err) {
      console.error("Error fetching status counts:", err);
    }
  };

  // ✅ Delete message - FIXED
  const deleteMessage = async (id) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      try {
        // Use the correct endpoint format
        await api.delete(`/api/ContactUs/${id}`);

        // Refresh the data
        await fetchMessages(currentPage, searchTerm, statusFilter);
        await fetchStatusCounts();

        alert("✅ Message deleted successfully");
      } catch (err) {
        console.error("Error deleting message:", err);
        alert("❌ Failed to delete message. Please check console for details.");
      }
    }
  };

  // ✅ Update message status
  const updateMessageStatus = async (id, newStatus) => {
    try {
      await api.patch(`/api/ContactUs/${id}/status`, { status: newStatus });
      // Update local state immediately
      setMessages(
        messages.map((msg) =>
          msg.id === id ? { ...msg, status: newStatus } : msg
        )
      );
      // Refresh counts
      fetchStatusCounts();
    } catch (err) {
      console.error("Error updating status:", err);
      alert("❌ Failed to update status");
    }
  };

  // ✅ Search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchMessages(1, value, statusFilter);
  };

  // ✅ Filter by status
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    fetchMessages(1, searchTerm, status);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchMessages(newPage, searchTerm, statusFilter);
    }
  };

  // Load data
  useEffect(() => {
    fetchMessages(currentPage, searchTerm, statusFilter);
    fetchStatusCounts();
    // eslint-disable-next-line
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "Resolved":
        return "#10b981";
      case "InProgress":
        return "#f59e0b";
      case "Pending":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Resolved":
        return <FiCheckCircle size={18} />;
      case "InProgress":
        return <FiClock size={18} />;
      case "Pending":
        return <FiAlertCircle size={18} />;
      default:
        return <FiMessageSquare size={18} />;
    }
  };

  return (
    <div className="messages-page">
      <div className="dashboard-header">
        <h1>Messages Management Dashboard</h1>
        <p>Manage all Messages in the system</p>
      </div>

      {/* Header */}
      <div className="page-header">
        <h1>
          <FiMail className="header-icon" />
          Contact Messages <span className="total-count">({totalCount})</span>
        </h1>

        <div className="header-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search messages..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {/* Stats Bar with Filter */}
      <div className="stats-bar">
        <div
          className={`stat-item ${statusFilter === "" ? "active" : ""}`}
          onClick={() => handleStatusFilter("")}
        >
          <span className="stat-count">{totalCount}</span>
          <span className="stat-label">All Messages</span>
        </div>

        <div
          className={`stat-item ${statusFilter === "Pending" ? "active" : ""}`}
          onClick={() => handleStatusFilter("Pending")}
        >
          <span className="stat-count" style={{ color: "#ef4444" }}>
            {statusCounts.Pending || 0}
          </span>
          <span className="stat-label">Pending</span>
        </div>

        <div
          className={`stat-item ${
            statusFilter === "InProgress" ? "active" : ""
          }`}
          onClick={() => handleStatusFilter("InProgress")}
        >
          <span className="stat-count" style={{ color: "#f59e0b" }}>
            {statusCounts.InProgress || 0}
          </span>
          <span className="stat-label">In Progress</span>
        </div>

        <div
          className={`stat-item ${statusFilter === "Resolved" ? "active" : ""}`}
          onClick={() => handleStatusFilter("Resolved")}
        >
          <span className="stat-count" style={{ color: "#10b981" }}>
            {statusCounts.Resolved || 0}
          </span>
          <span className="stat-label">Resolved</span>
        </div>
      </div>

      {/* Filter Indicator */}
      {statusFilter && (
        <div className="filter-indicator">
          <span>
            Showing messages with status: <strong>{statusFilter}</strong>
          </span>
          <button onClick={() => handleStatusFilter("")}>Clear Filter</button>
        </div>
      )}

      {/* Messages */}
      <div className="messages-container">
        {loading && (
          <div className="loading">
            <p>Loading messages...</p>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="no-messages">
            <FiMail size={48} />
            <p>No messages found</p>
            {statusFilter && (
              <button
                className="clear-filter-btn"
                onClick={() => handleStatusFilter("")}
              >
                Clear Filter
              </button>
            )}
          </div>
        )}

        {!loading &&
          messages.map((message) => (
            <div key={message.id} className="message-card">
              <div className="message-header">
                <div className="message-info">
                  <h3 className="message-name">{message.name}</h3>
                  <span className="message-date">
                    {new Date(message.submittedAt).toLocaleDateString()} •{" "}
                    {new Date(message.submittedAt).toLocaleTimeString()}
                  </span>
                </div>
                {message.billboard && (
                  <div className="billboard-details">
                    <p>Code: {message.billboard.code}</p>
                  </div>
                )}
                <div className="status-container">
                  {getStatusIcon(message.status)}
                  <select
                    value={message.status}
                    onChange={(e) =>
                      updateMessageStatus(message.id, e.target.value)
                    }
                    className="status-select"
                    style={{ borderColor: getStatusColor(message.status) }}
                  >
                    <option value="Pending">Pending</option>
                    <option value="InProgress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                  </select>
                </div>
              </div>

              <div className="message-content">
                <p>{message.message}</p>
              </div>

              <div className="message-footer">
                <div className="contact-info">
                  <a href={`mailto:${message.email}`} className="contact-link">
                    <FiMail /> {message.email}
                  </a>
                  {message.phone && (
                    <a href={`tel:${message.phone}`} className="contact-link">
                      📞 {message.phone}
                    </a>
                  )}
                </div>

                <div className="action-buttons">
                  <button
                    onClick={() => deleteMessage(message.id)}
                    className="action-btn delete-btn"
                  >
                    <FiTrash2 /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            <FiChevronLeft /> Previous
          </button>

          <div className="pagination-info">
            Page {currentPage} of {totalPages} • {messages.length} messages
            shown
          </div>

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
          >
            Next <FiChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default Messages;
